
import { CommonModule } from '@angular/common';
import { Component, Input, Output, EventEmitter, OnInit, ViewChild, ElementRef, OnDestroy, OnChanges, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';

type DropdownType = 'period' | 'status' | 'input';

@Component({
    selector: 'auto-complete-dropdown',
    standalone: true,
    imports: [CommonModule, FormsModule],
    templateUrl: './auto-complete-dropdown.component.html',
    styleUrls: ['./auto-complete-dropdown.component.scss']
})
export class AutoCompleteDropdownComponent<T = any> implements OnInit, OnDestroy, OnChanges {
    private static dropdownOpened = new EventEmitter<string>();

    @Input()
    options: T[] = [];

    @Input()
    placeholder: string = 'Select an option';

    @Input()
    label?: string;

    @Input()
    required: boolean = false;

    @Input()
    controlId: string = 'auto-complete-input';

    @Input()
    displayWith: (option: T) => string = (option) => `${(option as any).name}`;

    @Input()
    initialValue?: number;

    @Input()
    invalid: boolean = false;

    @Input()
    disabled: boolean = false;

    @Input()
    validationMessage: string = 'This field is required.';

    @Input()
    type: DropdownType = 'input';

    @Output()
    selectionChange = new EventEmitter<number | null>();

    @ViewChild('searchInput') searchInputRef!: ElementRef<HTMLInputElement>;
    @ViewChild('dropdownList') dropdownListRef!: ElementRef<HTMLElement>;

    public searchText: string = '';
    public filteredOptions: T[] = [];
    public dropdownVisible: boolean = false;
    public selectedOption: T | null = null;

    private dropdownId: string = crypto.randomUUID();
    private subscription!: Subscription;

    get showSearch(): boolean {
        return this.type !== 'status';
    }

    ngOnInit(): void {
        this.filteredOptions = [...this.options];
        this.setSelectedOptionFromInitialValue();
        this.subscription = AutoCompleteDropdownComponent.dropdownOpened.subscribe((id) => {
            if (id !== this.dropdownId) {
                this.dropdownVisible = false;
            }
        });
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['options'] || changes['initialValue']) {
            this.filteredOptions = [...this.options];
            this.setSelectedOptionFromInitialValue();
        }
    }

    private setSelectedOptionFromInitialValue(): void {
        if (this.initialValue !== undefined && this.initialValue !== null) {
            const match = this.options.find((o: any) => o.id === this.initialValue);
            if (match) {
                this.selectedOption = match;
                return;
            }
        }

        if (this.initialValue === undefined || this.initialValue === null) {
            this.selectedOption = null;
        }
    }

    ngOnDestroy(): void {
        this.subscription?.unsubscribe();
    }

    toggleDropdown(event: MouseEvent): void {
        if (this.disabled) {
            event.preventDefault();
            return;
        }
        event.preventDefault();
        this.dropdownVisible = !this.dropdownVisible;

        if (this.dropdownVisible) {
            AutoCompleteDropdownComponent.dropdownOpened.emit(this.dropdownId);

            this.searchText = '';
            this.filteredOptions = [...this.options];

            setTimeout(() => {
                if (this.showSearch && this.searchInputRef?.nativeElement) {
                    this.searchInputRef.nativeElement.focus();
                }

                if (this.dropdownListRef?.nativeElement) {
                    this.dropdownListRef.nativeElement.scrollTop = 0;
                }
            });
        }
    }

    onDropdownBlur(): void {
        setTimeout(() => {
            const active = document.activeElement as HTMLElement | null;
            const isInside = active?.closest('.autocomplete-dropdown') ||
                             active?.closest('.readonly-trigger');

            if (!isInside) {
                this.dropdownVisible = false;
            }
        }, 200);
    }

    onInputChange(): void {
        const query = this.searchText.trim().toLowerCase();

        if (!query) {
            this.filteredOptions = [...this.options];
        }
        else {
            this.filteredOptions = this.options.filter(option =>
                this.displayWith(option).toLowerCase().includes(query)
            );
        }

        if (this.filteredOptions.length === 1) {
            this.selectOption(this.filteredOptions[0]);
        }
    }

    selectOption(option: T): void {
        if (this.disabled) return;
        this.selectedOption = option;
        this.dropdownVisible = false;
        this.selectionChange.emit((option as any).id);
    }

    clearSelection(event: MouseEvent): void {
        if (this.type !== 'input') return;

        event.stopPropagation();
        this.selectedOption = null;
        this.searchText = '';
        this.dropdownVisible = false;
        this.selectionChange.emit(null);
    }
}